import { useState } from "react"
import { useParams } from "react-router-dom"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { UserAvatar } from "@/components/ui/user-avatar"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Trophy, Calendar, Flag, Filter, SearchX, ArchiveIcon, Info } from "lucide-react"
import { useAllGamesWithSeasons } from "@/graphql/hooks/useGames"
import { useRankingList } from "@/graphql/hooks/useSeasons"
import { useUrlPagination } from "@/hooks/useUrlPagination"
import { useCountries, useRegions } from "@/graphql/hooks/Locations/useLocations"
import { PodioPlayers } from "@/components/PodioPlayers"
import { CountryFlag } from "@/components/ui/country-flag"
import { useAuthContext } from "@/contexts/AuthContext"
import { motion } from "framer-motion"

const getWinRateColors = (winPct: number) => {
  if (winPct > 65) {
    return {
      bg: "bg-emerald-500",
      text: "text-emerald-600 dark:text-emerald-400",
      label: "Alto",
    }
  }

  if (winPct >= 35) {
    return {
      bg: "bg-amber-500",
      text: "text-amber-600 dark:text-amber-400",
      label: "Medio",
    }
  }

  return {
    bg: "bg-red-500",
    text: "text-red-600 dark:text-red-400",
    label: "Bajo",
  }
}

const SeasonDetail = () => {
  const { gameId, seasonId } = useParams<{
    gameId: string
    seasonId: string
  }>()
  const { currentPage, setPage } = useUrlPagination()
  const { isAdmin, isAuthenticated } = useAuthContext()
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(Number(seasonId))
  const [selectedCountryId, setSelectedCountryId] = useState<number>(0)

  const { games, loading: gamesLoading } = useAllGamesWithSeasons(true, {
    isAdmin,
    isAuthenticated,
  })
  const { countries, countriesMap } = useCountries()
  const { regionsMap, loading: regionsLoading } = useRegions(selectedCountryId)

  const currentGame = games.find((game) => game.id === Number(gameId))
  const seasons = currentGame?.Seasons || []
  const currentSeason = seasons.find((season) => season.id === selectedSeasonId)
  const selectedCountryName =
    selectedCountryId === 0
      ? "Todos los paises"
      : (countries?.find((country) => country.id === selectedCountryId)?.name ?? "Pais")

  const rankingInput = {
    seasonId: selectedSeasonId,
    filter: {
      countryId: selectedCountryId,
      regionId: 0,
      search: "",
      page: currentPage,
      limit: 32,
    },
  }

  const { data: rankingData, loading: rankingLoading } = useRankingList(rankingInput)

  const handleSeasonChange = (newSeasonId: string) => {
    setSelectedSeasonId(Number(newSeasonId))
    setPage(1)
  }

  const handleCountryChange = (countryId: string) => {
    setSelectedCountryId(Number(countryId))
    setPage(1)
  }

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  // Helper function to generate pagination items
  const generatePaginationItems = () => {
    if (!rankingData?.pages) return []

    const totalPages = rankingData.pages
    const items = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        items.push(i)
      }
    } else {
      // Complex pagination logic
      const startPage = Math.max(1, currentPage - 2)
      const endPage = Math.min(totalPages, currentPage + 2)

      // Always show first page
      if (startPage > 1) {
        items.push(1)
        if (startPage > 2) {
          items.push("ellipsis-start")
        }
      }

      // Show pages around current page
      for (let i = startPage; i <= endPage; i++) {
        items.push(i)
      }

      // Always show last page
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          items.push("ellipsis-end")
        }
        items.push(totalPages)
      }
    }

    return items
  }

  if (gamesLoading || rankingLoading || regionsLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        </div>
      </Layout>
    )
  }

  if (!currentGame) {
    return (
      <Layout>
        <div className="text-center">
          <h1 className="text-2xl font-bold">Juego no encontrado</h1>
        </div>
      </Layout>
    )
  }

  const podiumPlayers =
    currentPage === 1
      ? rankingData?.Ranking?.slice(0, 5).map((x) => {
          return {
            ...x,
            TournamentPlayer: x,
          }
        })
      : []

  return (
    <Layout>
      <div className="min-h-screen overflow-x-hidden">
        <section className="relative overflow-hidden px-4 pt-8 pb-4 md:pt-12 md:pb-6">
          <div className="mx-auto w-full">
            <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(320px,1fr)]">
              <Card className="relative overflow-hidden border-primary/15 bg-card/65 backdrop-blur-xl">
                {/* Game banner */}
                {currentGame.urlImage && (
                  <div className="relative h-32 overflow-hidden">
                    <img
                      src={currentGame.urlImage}
                      alt={currentGame.name}
                      className="h-full w-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-white drop-shadow leading-tight">
                          {currentGame.name}
                        </h2>
                        {currentGame.description && (
                          <p className="text-xs text-white/70 mt-0.5 line-clamp-1">
                            {currentGame.description}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {currentSeason?.isActive ? (
                          <Badge className="text-[10px] px-1.5 py-0 bg-emerald-500/90 text-white border-0">
                            Activa
                          </Badge>
                        ) : (
                          <Badge className="text-[10px] px-1.5 py-0 bg-zinc-800/80 text-zinc-200 border-0">
                            Finalizada
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <CardHeader className="space-y-4 pt-3">
                  {/* Season badge */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="border-primary/30 bg-primary/10 text-primary text-[10px] px-1.5 py-0">
                      Temporada seleccionada
                    </Badge>
                    <Badge
                      variant="outline"
                      className="max-w-full border-border/70 bg-background/50 sm:max-w-[24rem] text-xs"
                      title={currentSeason?.name || "Sin temporada"}
                    >
                      <Calendar className="mr-1 h-3 w-3 shrink-0" />
                      <span className="truncate">{currentSeason?.name || "Sin temporada"}</span>
                    </Badge>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="rounded-xl border border-border/70 bg-background/55 px-3 py-2 text-center">
                      <p className="text-xl font-bold tabular-nums">
                        {Number(currentGame.totalTournaments || 0)}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
                        Torneos
                      </p>
                    </div>
                    <div className="rounded-xl border border-border/70 bg-background/55 px-3 py-2 text-center">
                      <p className="text-xl font-bold tabular-nums">{seasons.length}</p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
                        Temporadas
                      </p>
                    </div>
                    <div className="rounded-xl border border-border/70 bg-background/55 px-3 py-2 text-center">
                      <p className="text-xl font-bold tabular-nums truncate">
                        {rankingData?.total ?? "--"}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
                        Jugadores
                      </p>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              <Card className="relative overflow-hidden border-border/70 bg-card/75 backdrop-blur-sm">
                {/* Filter panel header strip */}
                <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-muted/40 dark:border-gray-700">
                  <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Filtros
                  </span>
                </div>
                <CardContent className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Temporada
                    </p>
                    <Select value={selectedSeasonId.toString()} onValueChange={handleSeasonChange}>
                      <SelectTrigger className="w-full bg-background/70 [&>span]:max-w-[85%] [&>span]:truncate">
                        <SelectValue placeholder="Selecciona una temporada" />
                      </SelectTrigger>
                      <SelectContent>
                        {seasons.map((season) => (
                          <SelectItem key={season.id} value={season.id.toString()}>
                            <div className="flex min-w-0 items-center gap-2" title={season.name}>
                              <span className="max-w-[16rem] truncate">{season.name}</span>
                              {season.isActive > 0 && (
                                <Badge variant="secondary" className="ml-1 shrink-0">
                                  Activa
                                </Badge>
                              )}
                              {season.isArchived > 0 && (
                                <Badge variant="outline" className="ml-1 shrink-0">
                                  <ArchiveIcon className="mr-1 inline h-3 w-3" />
                                  Archivada
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Pais
                    </p>
                    <Select
                      value={selectedCountryId.toString()}
                      onValueChange={handleCountryChange}
                    >
                      <SelectTrigger className="w-full bg-background/70">
                        <SelectValue placeholder="Todos los paises" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Todos los paises</SelectItem>
                        {countries?.map((country) => (
                          <SelectItem key={country.id} value={country.id.toString()}>
                            <div className="flex items-center gap-2">{country.name}</div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="rounded-lg border border-border/70 bg-background/55 px-3 py-2 text-xs text-muted-foreground">
                    Cambiar los filtros reinicia la paginacion para mostrar resultados desde la
                    primera pagina.
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="px-4 pt-6 pb-10">
          <div className="mx-auto w-full space-y-6">
            <PodioPlayers players={podiumPlayers} />
            {/* Season Info and Rankings */}
            {currentSeason ? (
              <Tabs defaultValue="ranking" className="space-y-4">
                <TabsList className="h-auto w-full justify-start gap-1 bg-muted/50 p-1 md:w-fit">
                  <TabsTrigger value="ranking">
                    <Trophy className="h-4 w-4 inline mr-2" />
                    Ranking general
                  </TabsTrigger>
                  <TabsTrigger value="info">
                    <Info className="h-4 w-4 inline mr-2" />
                    Información
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="info">
                  <Card className="border-border/70 bg-card/70 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle>{currentSeason?.name}</CardTitle>
                      <CardDescription>{currentSeason?.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium">Fecha de inicio</h4>
                          <p className="text-sm text-muted-foreground">
                            {currentSeason?.startDate
                              ? new Date(currentSeason.startDate).toLocaleDateString("es-ES", {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "No especificada"}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium">Fecha de fin</h4>
                          <p className="text-sm text-muted-foreground">
                            {currentSeason?.endDate
                              ? new Date(currentSeason.endDate).toLocaleDateString("es-ES", {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "No especificada"}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium">Estado</h4>
                          <Badge
                            className={
                              currentSeason?.isActive
                                ? "bg-emerald-500/90 text-white"
                                : "bg-muted text-muted-foreground"
                            }
                          >
                            {currentSeason?.isActive ? "Activa" : "Finalizada"}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="ranking">
                  <Card className="border-border/70 bg-card/70 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="flex min-w-0 items-center gap-2">
                        <Trophy className="h-5 w-5 shrink-0" />
                        <span className="truncate" title={`Ranking - ${currentSeason.name}`}>
                          Ranking - {currentSeason.name}
                        </span>
                      </CardTitle>
                      <CardDescription>
                        {rankingData?.total > 0 && `${rankingData.total} jugadores en el ranking`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {rankingLoading ? (
                        <LoadingScreen message="Cargando ranking de la temporada..." />
                      ) : (
                        <div className="w-full overflow-x-auto">
                          <div className="w-full">
                            {!rankingData || rankingData?.total === 0 ? (
                              <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                                <SearchX className="mb-4 h-12 w-12 opacity-70" />
                                <h3 className="text-lg font-semibold">
                                  No se encontraron resultados
                                </h3>
                                <p className="text-sm opacity-80">
                                  Intenta ajustar los filtros o realizar otra búsqueda.
                                </p>
                              </div>
                            ) : (
                              <Table className="hidden w-full sm:table">
                                {/* 📌 Tabla visible solo en escritorio/tablet */}
                                <TableHeader className="bg-muted/60">
                                  <TableRow>
                                    <TableHead className="w-16 text-center">Pos.</TableHead>
                                    <TableHead>Jugador</TableHead>
                                    <TableHead>País / Región</TableHead>
                                    <TableHead className="text-center">Puntos</TableHead>
                                    <TableHead className="text-center">Torneos</TableHead>
                                    <TableHead className="text-center">Winrate</TableHead>
                                    <TableHead className="text-center">V/E/D</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {rankingData?.Ranking?.map((player, index) => {
                                    const position = (currentPage - 1) * 32 + index + 1

                                    return (
                                      <TableRow
                                        key={player.id}
                                        className="odd:bg-background/35 even:bg-muted/20"
                                      >
                                        <TableCell className="flex justify-center text-center">
                                          {position <= 3 ? (
                                            <Trophy
                                              className={`w-6 h-6 ${
                                                position === 1
                                                  ? "text-yellow-500"
                                                  : position === 2
                                                    ? "text-gray-400"
                                                    : "text-amber-600"
                                              }`}
                                            />
                                          ) : (
                                            <span className="text-center">{position}</span>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <div className="flex items-center gap-3">
                                            <UserAvatar
                                              src={player.Person.urlAvatar || player.Person.avatar}
                                              name={player.Person.fullName}
                                              size="sm"
                                            />
                                            <div>
                                              <p className="font-medium">
                                                {player.Person.fullName}
                                              </p>
                                              {isAdmin && (
                                                <p className="text-sm text-muted-foreground">
                                                  {player.Person.code}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                          <div className="flex items-center gap-1">
                                            <span className="text-sm">
                                              <CountryFlag
                                                countryCode={
                                                  countriesMap.get(player.Person.countryId).iso
                                                }
                                              />
                                              {regionsMap.get(player.Person.regionId)?.name}
                                            </span>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-center font-bold">
                                          {player.points}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {player.totalTournaments}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {(() => {
                                            const total =
                                              player.victories + player.defeats + player.draws
                                            const winPct =
                                              total > 0
                                                ? Math.round(
                                                    ((player.victories + player.draws / 2) /
                                                      total) *
                                                      100,
                                                  )
                                                : 0
                                            const colors = getWinRateColors(winPct)
                                            return (
                                              <div className="flex items-center justify-center gap-2">
                                                <div className="w-12 h-1 bg-muted rounded-full overflow-hidden">
                                                  <motion.div
                                                    className={`h-full ${colors.bg}`}
                                                    initial={{ width: "0%" }}
                                                    animate={{ width: `${winPct}%` }}
                                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                                  />
                                                </div>
                                                <span
                                                  className={`text-xs font-semibold w-8 text-right ${colors.text}`}
                                                >
                                                  {winPct}%
                                                </span>
                                              </div>
                                            )
                                          })()}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          <span className="text-green-600 dark:text-green-400">
                                            {player.victories}
                                          </span>
                                          /
                                          <span className="text-gray-500 dark:text-gray-400">
                                            {player.draws}
                                          </span>
                                          /
                                          <span className="text-red-600 dark:text-red-400">
                                            {player.defeats}
                                          </span>
                                        </TableCell>
                                      </TableRow>
                                    )
                                  })}
                                </TableBody>
                              </Table>
                            )}

                            {/* 📌 Cards visibles solo en mobile */}
                            <div className="sm:hidden space-y-3">
                              {rankingData?.Ranking?.map((player, index) => {
                                const position = (currentPage - 1) * 20 + index + 1

                                return (
                                  <div
                                    key={player.id}
                                    className="rounded-xl border border-border/70 bg-card/80 p-4 shadow-sm"
                                  >
                                    <div className="flex justify-center items-center gap-2">
                                      <UserAvatar
                                        src={player.Person.urlAvatar || player.Person.avatar}
                                        name={player.Person.fullName}
                                        size="lg"
                                      />
                                    </div>
                                    {/* Posición */}
                                    <div className="flex justify-between mb-2">
                                      <span className="font-semibold text-muted-foreground">
                                        Posición
                                      </span>
                                      {position <= 3 ? (
                                        <Trophy
                                          className={`w-6 h-6 ${
                                            position === 1
                                              ? "text-yellow-500"
                                              : position === 2
                                                ? "text-gray-400"
                                                : "text-amber-600"
                                          }`}
                                        />
                                      ) : (
                                        <span className="font-medium">{`#${position}`}</span>
                                      )}
                                    </div>

                                    {/* Jugador */}
                                    <div className="flex justify-between mb-2">
                                      <span className="font-semibold text-muted-foreground">
                                        Jugador
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <div className="text-right">
                                          <p className="font-medium">{player.Person.fullName}</p>
                                        </div>
                                      </div>
                                    </div>
                                    {/* Código */}
                                    <div className="flex justify-between mb-2">
                                      <span className="font-semibold text-muted-foreground">
                                        Código jugador
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <div className="text-right">
                                          <p className="font-medium">{player.Person.code}</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* País */}
                                    <div className="flex justify-between mb-2">
                                      <span className="font-semibold text-muted-foreground">
                                        País
                                      </span>
                                      <div className="flex items-center gap-1">
                                        {player.Person.Address?.Country?.iso && (
                                          <Flag className="w-4 h-4" />
                                        )}
                                        <span className="text-sm">
                                          {player.Person.Address?.Country?.name || "N/A"}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Puntos */}
                                    <div className="flex justify-between mb-2">
                                      <span className="font-semibold text-muted-foreground">
                                        Puntos
                                      </span>
                                      <span className="font-bold">{player.points}</span>
                                    </div>

                                    {/* Torneos */}
                                    <div className="flex justify-between mb-2">
                                      <span className="font-semibold text-muted-foreground">
                                        Torneos
                                      </span>
                                      {player.totalTournaments}
                                    </div>

                                    {/* Winrate */}
                                    {(() => {
                                      const total = player.victories + player.losses + player.draws
                                      const winPct =
                                        total > 0
                                          ? Math.round(
                                              ((player.victories + player.draws / 2) / total) * 100,
                                            )
                                          : 0
                                      const colors = getWinRateColors(winPct)
                                      return (
                                        <div className="flex justify-between items-center mb-2">
                                          <span className="font-semibold text-muted-foreground text-sm">
                                            Winrate
                                          </span>
                                          <div className="flex items-center gap-2">
                                            <div className="w-16 h-1 bg-muted rounded-full overflow-hidden">
                                              <motion.div
                                                className={`h-full ${colors.bg}`}
                                                initial={{ width: "0%" }}
                                                animate={{ width: `${winPct}%` }}
                                                transition={{ duration: 0.8, ease: "easeOut" }}
                                              />
                                            </div>
                                            <span
                                              className={`text-xs font-semibold w-8 text-right ${colors.text}`}
                                            >
                                              {winPct}%
                                            </span>
                                          </div>
                                        </div>
                                      )
                                    })()}

                                    {/* Marcador */}
                                    <div className="flex justify-between">
                                      <span className="font-semibold text-muted-foreground">
                                        V/E/D
                                      </span>
                                      <span>
                                        <span className="text-green-600 font-medium">
                                          {player.victories}
                                        </span>
                                        /
                                        <span className="text-muted-foreground font-medium">
                                          {player.draws}
                                        </span>
                                        /
                                        <span className="text-red-600 font-medium">
                                          {player.defeats}
                                        </span>
                                      </span>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>

                          {/* Pagination */}
                          {rankingData?.pages > 1 && (
                            <>
                              <div className="hidden md:flex mt-6">
                                <Pagination>
                                  <PaginationContent>
                                    {currentPage > 1 && (
                                      <PaginationItem>
                                        <PaginationPrevious
                                          onClick={() => handlePageChange(currentPage - 1)}
                                          className="cursor-pointer"
                                        >
                                          Anterior
                                        </PaginationPrevious>
                                      </PaginationItem>
                                    )}

                                    {generatePaginationItems().map((item, index) => {
                                      if (item === "ellipsis-start" || item === "ellipsis-end") {
                                        return (
                                          <PaginationItem key={`ellipsis-${index}`}>
                                            <PaginationEllipsis />
                                          </PaginationItem>
                                        )
                                      }

                                      const pageNumber = item as number
                                      return (
                                        <PaginationItem key={pageNumber}>
                                          <PaginationLink
                                            onClick={() => handlePageChange(pageNumber)}
                                            isActive={currentPage === pageNumber}
                                            className="cursor-pointer"
                                          >
                                            {pageNumber}
                                          </PaginationLink>
                                        </PaginationItem>
                                      )
                                    })}

                                    {currentPage < rankingData.pages && (
                                      <PaginationItem>
                                        <PaginationNext
                                          onClick={() => handlePageChange(currentPage + 1)}
                                          className="cursor-pointer"
                                        >
                                          Siguiente
                                        </PaginationNext>
                                      </PaginationItem>
                                    )}
                                  </PaginationContent>
                                </Pagination>
                              </div>
                              <div className="mt-6 flex md:hidden">
                                <Pagination>
                                  {/* Mobile: solo botones Anterior / Siguiente */}
                                  <PaginationContent className="flex sm:hidden justify-between w-full">
                                    {currentPage > 1 && (
                                      <PaginationItem>
                                        <PaginationPrevious
                                          onClick={() => handlePageChange(currentPage - 1)}
                                          className="cursor-pointer"
                                        >
                                          Anterior
                                        </PaginationPrevious>
                                      </PaginationItem>
                                    )}
                                    {currentPage < rankingData.pages && (
                                      <PaginationItem>
                                        <PaginationNext
                                          onClick={() => handlePageChange(currentPage + 1)}
                                          className="cursor-pointer"
                                        >
                                          Siguiente
                                        </PaginationNext>
                                      </PaginationItem>
                                    )}
                                  </PaginationContent>

                                  {/* Desktop: paginación completa */}
                                  <PaginationContent className="hidden sm:flex">
                                    {/* aquí dejas lo mismo que ya tienes con números */}
                                  </PaginationContent>
                                </Pagination>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="w-full flex justify-center mt-6">
                <div className="max-w-md w-full rounded-xl border border-border/70 bg-card/80 shadow-md p-6 flex flex-col items-center text-center space-y-3">
                  <svg
                    className="w-10 h-10 text-yellow-500"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20 10 10 0 010-20z"
                    />
                  </svg>
                  <h3 className="text-lg font-semibold text-foreground">
                    No hay datos de la temporada
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Por el momento no hay datos de la temporada disponible. Revisa más tarde para
                    ver los rankings y torneos activos.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Glosa de Winrate */}
          <div className="mt-8  mx-auto">
            <Card className="border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">
                      ¿Cómo se calcula el Winrate?
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      El winrate es el porcentaje de efectividad del jugador considerando victorias
                      y empates. Se calcula como:{" "}
                      <span className="font-mono bg-muted px-2 py-1 rounded">
                        (Victorias + Empates/2) / Total de Partidas × 100
                      </span>
                      <br className="mt-2" />
                      Los empates se cuentan como medio punto, ya que representan un resultado
                      neutral. Por ejemplo, si un jugador tiene 10 victorias, 2 empates y 3 derrotas
                      (15 partidas totales), su winrate sería (10 + 2/2) / 15 × 100 = (11/15) × 100
                      = 73.33%.
                      <br className="mt-2" />
                      <span className="text-xs">
                        <span className="inline-block mr-3">
                          <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full mr-1.5"></span>
                          Verde: winrate alto (&gt;65%)
                        </span>
                        <span className="inline-block mr-3">
                          <span className="inline-block w-2 h-2 bg-amber-500 rounded-full mr-1.5"></span>
                          Amarillo: winrate medio (35-65%)
                        </span>
                        <span className="inline-block">
                          <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1.5"></span>
                          Rojo: winrate bajo (&lt;35%)
                        </span>
                      </span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default SeasonDetail
